import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { Store } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { routerSegments } from '@consts/routes';
import { IntelligenceModels } from '@models/intelligence.model';
import { AlertV2Document } from '@models/alerts-v2.model';
import { WallV2Model } from '@models/wall-v2.model';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { EdgeSelectors } from '@states/edge/edge.selector-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { WallV2Actions } from '@states/wall-v2/wall-v2.action-types';
import { WallV2Selectors } from '@states/wall-v2/wall-v2.selector-types';
import type { WallV2State } from '@states/wall-v2/wall-v2.reducer';
import { UiCameraSelectButtonComponent } from '@ui';
import { UiV2SelectorComponent } from '@ui-kit/ui-v2-selector/ui-v2-selector.component';
import { CameraPickerModalComponent } from '@modals/camera-picker-modal/camera-picker-modal.component';
import { CameraPickerModels } from '@modals/camera-picker-inline/models/models';
import { WallV2Module } from '../../../../../../pages/wall-v2/wall-v2.module';
import { V2SelectLayoutModalComponent } from '../../../../../../pages/wall-v2/modals/v2-select-layout-modal/v2-select-layout-modal.component';
import { WallSettingsModalComponent } from '../../../../../../pages/wall-v2/modals/wall-settings-modal/wall-settings-modal.component';
import { WallSetSettingsModalComponent } from '../../../../../../pages/wall-v2/modals/wall-set-settings-modal/wall-set-settings-modal.component';
import { AppState } from '../../../../../../store/app.state';
import { EdgeCamera } from '../../../../../../cameras/camera.model';
import { UtilsService } from '../../../../../../edge/utils.service';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import { WallPlaybackSyncService } from '../../../../../../services/wall-playback-sync.service';

@Component({
  selector: 'app-create-wall-result',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    MatSelectModule,
    MatTooltipModule,
    UiCameraSelectButtonComponent,
    UiV2SelectorComponent,
    WallV2Module,
    CdkDropListGroup,
  ],
  templateUrl: './create-wall-result.component.html',
  styleUrls: ['./create-wall-result.component.scss'],
  providers: [WallPlaybackSyncService],
})
export class CreateWallResultComponent implements OnDestroy {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly toolsService = inject(IntelligenceToolsService);
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly utilsService = inject(UtilsService);

  protected readonly name = signal<string>('');
  protected readonly selectedCameras = signal<Dictionary<CameraPickerModels.SelectedCamera>>({});
  protected readonly tileCameraIds = signal<(string | null)[]>(new Array(9).fill(null));
  protected readonly tileAlerts = signal<(WallV2Model.TileAlertData | null)[]>(new Array(9).fill(null));
  protected readonly tileFixedSettings = signal<(WallV2Model.TileSettings | null)[]>(new Array(9).fill(null));
  protected readonly layout = signal<WallV2Model.WallLayout>(WallV2Model.WallLayout.GRID_9);
  protected readonly selectedSetIndex = signal<number>(0);
  protected readonly wallSets = signal<WallV2Model.WallSet[]>([]);
  protected readonly wallSettings = signal<WallV2Model.WallSettings>(this.cloneWallSettings(
    WallV2Model.defaultWallCreate.settings,
  ));
  protected readonly isSaving = signal<boolean>(false);
  protected readonly isCancelling = signal<boolean>(false);
  protected readonly alertsControl = new FormControl<string[]>([], { nonNullable: true });

  protected readonly cameraEntities = this.store$.selectSignal(CameraSelectors.selectAllCamerasEntities);
  protected readonly alertEvents = this.store$.selectSignal(WallV2Selectors.selectAlertEvents);
  private readonly edgeEntities = this.store$.selectSignal(EdgeSelectors.selectAllEdgesEntities);
  private readonly selectedStoreSet = this.store$.selectSignal(WallV2Selectors.selectCurrentSet);
  protected readonly selectHasWallAlert = this.store$.selectSignal(WallV2Selectors.selectHasWallAlert);

  /**
   * Pre-widget copy of the shared wall-v2 slice, captured before this component
   * dispatches anything. Restored on destroy so a wall page open underneath the
   * Cmd+K modal (list/edit/view) gets its state back instead of a wiped slice.
   */
  private readonly wallStateSnapshot: WallV2State = this.store$.selectSignal(WallV2Selectors.selectWallV2State)();

  protected readonly draft = computed<IntelligenceModels.Walls.CreateDraft | null>(() => {
    const result = this.resultsService.current();
    return result?.type === 'create-wall'
      ? (result.data as IntelligenceModels.Walls.CreateDraft)
      : null;
  });

  private readonly editWall = computed<WallV2Model.WallMongoDocument | null>(() =>
    this.draft()?.wall ?? null,
  );

  protected readonly selectedCameraList = computed<CameraPickerModels.SelectedCamera[]>(() =>
    this.tileCameraIds()
      .map((cameraId) => (cameraId ? this.selectedCameras()[cameraId] : null))
      .filter((camera): camera is CameraPickerModels.SelectedCamera => !!camera),
  );

  protected readonly currentSetIndex = computed<number>(() => {
    const sets = this.wallSets();
    if (!sets.length) return 0;

    const index = this.selectedSetIndex();
    return Math.min(Math.max(index, 0), sets.length - 1);
  });

  protected readonly selectWallSetIndex = computed<number>(() => this.currentSetIndex());

  protected readonly selectWallSets = computed<WallV2Model.WallSet[]>(() =>
    this.wallSets(),
  );

  protected readonly hasMultipleSets = computed<boolean>(() =>
    this.wallSets().length > 1,
  );

  private readonly baseSelectedSet = computed<WallV2Model.WallSet>(() =>
    this.wallSets()[this.currentSetIndex()] ?? this.createDefaultWallSet(this.currentSetIndex()),
  );

  protected readonly tileCount = computed<number>(() =>
    WallV2Model.wallLayoutCameraCountV2[this.layout()] || 9,
  );

  protected readonly alertOptions = computed<WallV2Model.AlertSelectorOption[]>(() => [
    {
      _id: WallV2Model.WallAlertSelectorOptionId.ALL_ALERTS,
      name: this.translate.instant('WALL_CREATE.ALERTS_PANEL.ALL_ALERTS'),
    },
    ...(this.alertEvents() ?? [])
      .filter((alert): alert is AlertV2Document & { _id: string } => !!alert._id)
      .map((alert) => ({ _id: alert._id, name: alert.name })),
  ]);

  private readonly alertOccupiedCellCount = computed<number>(() =>
    this.tileAlerts().filter((alert) => !!alert).length,
  );

  protected readonly cameraCapacity = computed<number>(() =>
    this.tileCount() - this.alertOccupiedCellCount(),
  );

  protected readonly freeCellIndexes = computed<number[]>(() => {
    const cameraIds = this.tileCameraIds();
    const alerts = this.tileAlerts();
    const existingTiles = this.baseSelectedSet().tiles ?? [];

    return new Array(this.tileCount()).fill(null)
      .map((_value, index) => index)
      .filter((index) => !cameraIds[index] && !alerts[index] && !existingTiles[index]?.youtubeId);
  });

  protected readonly selectedAlertIds = computed<string[]>(() => {
    const ids = new Set<string>();
    const alerts = this.tileAlerts();

    if (alerts.some((alert) => alert?.allEvents)) {
      ids.add(WallV2Model.WallAlertSelectorOptionId.ALL_ALERTS);
    }
    alerts.forEach((alert) => alert?.events?.forEach((event) => ids.add(event.eventId)));

    return [...ids];
  });

  protected readonly alertSelectorHasNoFreeCells = computed<boolean>(() =>
    this.freeCellIndexes().length === 0,
  );

  protected readonly alertOptionsDisabledMap = computed<Map<string, boolean>>(() => {
    if (this.freeCellIndexes().length) return new Map<string, boolean>();

    const selectedIds = new Set(this.selectedAlertIds());
    return new Map(this.alertOptions().map((option) => [option._id, !selectedIds.has(option._id)]));
  });

  protected readonly tiles = computed<WallV2Model.SetTile[]>(() => {
    const existingTiles = this.baseSelectedSet().tiles ?? [];

    return new Array(this.tileCount()).fill(null).map((_tile, index) => {
      const camera = this.selectedCameraAt(index);
      const alert = this.tileAlerts()[index];
      const fixedSettings = this.tileFixedSettings()[index];
      const existingTile = existingTiles[index] ?? WallV2Model.defaultSetTile;
      const existingCamera = existingTile.camera?.cameraId === this.tileCameraIds()[index]
        ? existingTile.camera
        : null;
      const tile: WallV2Model.SetTile = {
        ...existingTile,
        camera: camera
          ? {
              cameraId: camera.cameraId,
              edgeId: camera.edgeId,
              locationId: camera.locationId,
            }
          : existingCamera
            ? { ...existingCamera }
            : WallV2Model.defaultSetTile.camera,
        events: alert?.events?.map((event) => ({ ...event })) ?? WallV2Model.defaultSetTile.events,
        allEvents: alert?.allEvents ?? false,
      };

      if (alert) {
        tile.camera = WallV2Model.defaultSetTile.camera;
        tile.youtubeId = WallV2Model.defaultSetTile.youtubeId;
      }

      if (fixedSettings) {
        tile.fixedSettings = { ...fixedSettings };
      } else {
        delete tile.fixedSettings;
      }

      return tile;
    });
  });

  protected readonly currentSet = computed<WallV2Model.WallSet>(() => ({
    ...this.baseSelectedSet(),
    layout: this.layout(),
    tiles: this.tiles(),
  }));

  protected readonly selectedSetLabel = computed<string>(() =>
    this.currentSet().name ||
    `${this.translate.instant('SEARCH_GLOBAL_MODAL.WALL_CAMERA_RESULT.SET')} ${this.currentSetIndex() + 1}`,
  );

  private readonly committedWallSets = computed<WallV2Model.WallSet[]>(() =>
    this.replaceSetAt(this.wallSets(), this.currentSetIndex(), this.currentSet()),
  );

  private readonly wallCreateDraft = computed<WallV2Model.WallCreateDto>(() => ({
    ...WallV2Model.defaultWallCreate,
    name: this.name(),
    settings: this.wallSettings(),
    sets: this.wallSets(),
  }));

  private readonly selectedWallDraft = computed<WallV2Model.WallCreateDto | WallV2Model.WallMongoDocument>(() => {
    const wall = this.editWall();
    if (!wall) return this.wallCreateDraft();

    return {
      ...wall,
      name: this.name(),
      settings: this.wallSettings(),
      sets: this.wallSets(),
    };
  });

  private lastDraftKey = '';
  private lastHydratedSetKey = '';

  public constructor() {
    // Raised after the snapshot above is captured, so the restore on destroy
    // clears it. Wall pages hide their canvas while this widget owns the slice.
    this.store$.dispatch(WallV2Actions.setWallWidgetActive({ active: true }));
    this.resultsService.setHeaderActions([
      {
        kind: IntelligenceModels.HeaderActionKind.BUTTON_DEFAULT,
        label: 'COMMON.CANCEL',
        tooltip: 'COMMON.CANCEL',
        onClick: () => void this.cancel(),
        disabled: () => this.isSaving(),
        loading: () => this.isCancelling(),
      },
      {
        kind: IntelligenceModels.HeaderActionKind.BUTTON_PRIMARY,
        label: 'COMMON.SAVE',
        tooltip: 'COMMON.SAVE',
        onClick: () => void this.save(),
        disabled: () => this.isCancelling(),
        loading: () => this.isSaving(),
      },
      {
        kind: IntelligenceModels.HeaderActionKind.ICON,
        icon: 'ui-icon-new-tab',
        tooltip: 'SEARCH_GLOBAL_MODAL.WALL_RESULT.OPEN_WALL_EDITOR',
        onClick: () => this.openWallEditorPage(),
        disabled: () => !this.editWall(),
      },
    ]);
    this.store$.dispatch(WallV2Actions.getAlertEvents());

    this.alertsControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids) => this.onAlertSelectionChanged(ids));

    effect(() => {
      const draft = this.draft();
      if (!draft) return;

      const editWall = draft.wall;
      const cameraIds = draft.cameraIds ?? [];
      const draftKey = editWall
        ? `edit|${editWall._id}|${editWall.updatedAt}|${editWall.name}`
        : `create|${draft.name ?? ''}|${cameraIds.join(',')}`;
      if (draftKey === this.lastDraftKey) return;

      const cameraEntities = this.cameraEntities();
      if (!editWall && cameraIds.length && !Object.keys(cameraEntities).length) return;

      this.lastDraftKey = draftKey;

      if (editWall) {
        this.hydrateWallForEdit(editWall);
        return;
      }

      this.name.set(draft.name ?? '');
      this.layout.set(WallV2Model.WallLayout.GRID_9);
      this.tileAlerts.set(this.fitTileAlerts([]));
      const initialCameras = this.resolveInitialCameras(cameraIds);
      this.selectedCameras.set(initialCameras);
      this.tileCameraIds.set(this.toTileCameraIds(initialCameras));
      this.tileFixedSettings.set(this.fitTileFixedSettings([]));
      this.wallSettings.set(this.cloneWallSettings(WallV2Model.defaultWallCreate.settings));
      this.wallSets.set([this.createSetFromCurrentState(0)]);
      this.selectedSetIndex.set(0);
      this.store$.dispatch(WallV2Actions.setSelectedSetIndex({ index: 0 }));
    });

    effect(() => {
      this.store$.dispatch(WallV2Actions.setSelectedWall({ wall: this.selectedWallDraft() }));
    });

    effect(() => {
      const set = this.selectedStoreSet();
      if (!set) return;

      const index = this.currentSetIndex();
      const localSet = this.wallSets()[index];
      if (!localSet || !this.areWallSetsEqual(localSet, set)) {
        this.wallSets.update((sets) => this.replaceSetAt(sets, index, set));
      }
    });

    effect(() => {
      const set = this.baseSelectedSet();
      const key = `${this.currentSetIndex()}|${Object.keys(this.cameraEntities()).length}|${JSON.stringify(set)}`;
      if (key === this.lastHydratedSetKey) return;

      this.lastHydratedSetKey = key;
      this.hydrateSelectedSet(set);
    });

    effect(() => {
      const set = this.selectedStoreSet();
      if (!set) return;

      const nextSettings = this.fitTileFixedSettings(
        (set.tiles ?? []).map((tile) => tile?.fixedSettings ? { ...tile.fixedSettings } : null),
      );

      if (this.areTileFixedSettingsEqual(this.tileFixedSettings(), nextSettings)) return;

      this.tileFixedSettings.set(nextSettings);
    });

    effect(() => {
      const selectedIds = this.selectedAlertIds();
      const controlIds = this.alertsControl.value;
      if (
        selectedIds.length === controlIds.length &&
        selectedIds.every((id, index) => id === controlIds[index])
      ) {
        return;
      }

      this.alertsControl.setValue(selectedIds, { emitEvent: false });
    });
  }

  public ngOnDestroy(): void {
    this.resultsService.setHeaderActions([]);
    this.store$.dispatch(WallV2Actions.restoreStateSnapshot({ snapshot: this.wallStateSnapshot }));
  }

  protected onNameInput(event: Event): void {
    this.name.set((event.target as HTMLInputElement).value);
  }

  protected openSelectCameraModal(): void {
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: {
        multi: true,
        maxSelect: this.cameraCapacity(),
      },
      selectedValue: this.selectedCameras(),
    };

    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '80vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((cameras?: Dictionary<CameraPickerModels.SelectedCamera>) => {
        if (!cameras) return;
        const nextCameras = this.limitSelectedCameras(cameras);
        this.selectedCameras.set(nextCameras);
        this.tileCameraIds.set(this.toTileCameraIds(nextCameras));
        this.tileFixedSettings.update((settings) => this.fitTileFixedSettings(settings));
        this.commitCurrentSet();
      });
  }

  protected openLayoutConfigModal(): void {
    this.dialog
      .open(V2SelectLayoutModalComponent, {
        panelClass: 'modal-no-padding',
        width: '685px',
        height: '540px',
        disableClose: true,
        data: {
          initialLoading: true,
        },
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((res?: { selectedLayout?: WallV2Model.WallLayout }) => {
        if (!res?.selectedLayout) return;
        this.applyLayout(res.selectedLayout);
      });
  }

  protected openSetSettingsModal(): void {
    this.commitCurrentSet();
    this.dialog
      .open(WallSetSettingsModalComponent, {
        panelClass: 'modal-no-padding',
        width: '680px',
        height: '797px',
        disableClose: true,
        data: {
          initialLoading: true,
        },
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((sets?: WallV2Model.WallSet[]) => {
        if (!sets) return;

        const nextSets = sets.map((set) => this.cloneWallSet(set));
        this.wallSets.set(nextSets);
        this.selectedSetIndex.set(Math.max(0, Math.min(this.currentSetIndex(), nextSets.length - 1)));
        this.store$.dispatch(WallV2Actions.updateSets({ sets }));
      });
  }

  protected changeSet(event: MatSelectChange): void {
    if (event.value === this.currentSetIndex()) return;

    this.commitCurrentSet();
    this.selectedSetIndex.set(event.value);
    this.store$.dispatch(WallV2Actions.setSelectedSetIndex({ index: event.value }));
  }

  protected openWallSettings(): void {
    this.dialog
      .open(WallSettingsModalComponent, {
        panelClass: 'modal-no-padding',
        width: '680px',
        height: '520px',
        disableClose: true,
        data: {
          initialLoading: true,
        },
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((res?: WallV2Model.WallSettings) => {
        if (!res) return;

        this.applyWallSettings(res);
      });
  }

  protected cleanTile(tile: number): void {
    this.tileCameraIds.update((ids) => {
      const next = this.fitTileCameraIds(ids);
      next[tile] = null;
      return next;
    });
    this.tileFixedSettings.update((settings) => {
      const next = this.fitTileFixedSettings(settings);
      next[tile] = null;
      return next;
    });
    this.tileAlerts.update((alerts) => {
      const next = this.fitTileAlerts(alerts);
      next[tile] = null;
      return next;
    });
    this.selectedCameras.update((cameras) => this.keepCamerasUsedByTiles(cameras));
    this.commitCurrentSet();
  }

  protected onTileDragged(event: WallV2Model.OnTileDrag): void {
    const draggedCameraId = this.tileCameraIds()[event.fromTile] ?? null;
    if (!draggedCameraId && !this.tileAlerts()[event.fromTile]) return;

    this.tileCameraIds.update((ids) => {
      const next = this.fitTileCameraIds(ids);
      next[event.fromTile] = next[event.tile] ?? null;
      next[event.tile] = draggedCameraId;
      return next;
    });
    this.tileAlerts.update((alerts) => {
      const next = this.fitTileAlerts(alerts);
      const draggedAlert = next[event.fromTile] ?? null;
      next[event.fromTile] = next[event.tile] ?? null;
      next[event.tile] = draggedAlert;
      return next;
    });
    this.tileFixedSettings.update((settings) => {
      const next = this.fitTileFixedSettings(settings);
      const draggedSettings = next[event.fromTile] ?? null;
      next[event.fromTile] = next[event.tile] ?? null;
      next[event.tile] = draggedSettings;
      return next;
    });
    this.commitCurrentSet();
  }

  protected onTileEventRemoved(event: { eventId: string; cameraId: string; tile: number }): void {
    this.tileAlerts.update((alerts) => {
      const next = this.fitTileAlerts(alerts);
      const tileAlert = next[event.tile];
      if (!tileAlert?.events) return next;

      const events = tileAlert.events.filter((selectedEvent) => selectedEvent.eventId !== event.eventId);
      next[event.tile] = events.length ? { events, allEvents: false } : null;
      return next;
    });
    this.commitCurrentSet();
  }

  protected onTileEventsChanged(event: { events: WallV2Model.SelectedEvent[]; tile: number }): void {
    this.tileAlerts.update((alerts) => {
      const next = this.fitTileAlerts(alerts);
      next[event.tile] = event.events.length
        ? { events: event.events.map((selectedEvent) => ({ ...selectedEvent })), allEvents: false }
        : null;
      return next;
    });
    this.commitCurrentSet();
  }

  protected async save(): Promise<void> {
    if (this.isSaving() || this.isCancelling()) return;

    this.commitCurrentSet();
    const name =
      this.name().trim() ||
      this.translate.instant('WALL_CREATE.LABELS.UNTITLED_WALL');
    const draft = {
      ...this.wallCreateDraft(),
      name,
    };
    const editWall = this.editWall();
    this.isSaving.set(true);
    try {
      const response = editWall
        ? await this.toolsService.updateWallFromDraft(this.buildWallUpdateDocument(editWall, draft))
        : await this.toolsService.createWallFromDraft(draft);
      this.store$.dispatch(
        SharedActions.showMessage({
          success: this.translate.instant(
            editWall
              ? 'SEARCH_GLOBAL_MODAL.WALL_RESULT.UPDATE_SUCCESS'
              : 'SEARCH_GLOBAL_MODAL.WALL_RESULT.CREATE_SUCCESS',
          ),
        }),
      );
      this.resultsService.replaceCurrentWithWalls(response, `${response.count} walls`);
    } catch (err) {
      this.store$.dispatch(SharedActions.showMessage({ error: this.utilsService.errMessage(err as Error) }));
    } finally {
      this.isSaving.set(false);
    }
  }

  /** Open the wall's full edit page in a new tab so the Cmd+K session stays open. */
  protected openWallEditorPage(): void {
    const wallId = this.editWall()?._id;
    if (!wallId) return;

    const tree = this.router.createUrlTree(['/', routerSegments.wallV2, routerSegments.edit, wallId]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected async cancel(): Promise<void> {
    if (this.isSaving() || this.isCancelling()) return;

    this.isCancelling.set(true);
    try {
      const response = await this.toolsService.fetchWalls({
        limit: IntelligenceModels.Walls.DEFAULT_LIMIT,
      });
      this.resultsService.replaceCurrentWithWalls(response, `${response.count} walls`);
    } catch (err) {
      this.store$.dispatch(SharedActions.showMessage({ error: this.utilsService.errMessage(err as Error) }));
    } finally {
      this.isCancelling.set(false);
    }
  }

  private onAlertSelectionChanged(ids: string[]): void {
    const currentIds = this.selectedAlertIds();
    const nextIds = new Set(ids);
    const currentIdsSet = new Set(currentIds);
    const removals = currentIds.filter((id) => !nextIds.has(id));
    const additions = ids.filter((id) => !currentIdsSet.has(id));

    removals.forEach((id) => this.removeAlertFromTiles(id));
    additions.forEach((id) => this.placeAlertInFirstFreeCell(id));

    if (removals.length || additions.length) {
      this.commitCurrentSet();
    }
  }

  private removeAlertFromTiles(id: string): void {
    this.tileAlerts.update((alerts) => this.fitTileAlerts(alerts).map((alert) => {
      if (!alert) return null;
      if (id === WallV2Model.WallAlertSelectorOptionId.ALL_ALERTS) {
        return alert.allEvents ? null : alert;
      }
      if (alert.allEvents) return alert;

      const events = alert.events?.filter((event) => event.eventId !== id) ?? null;
      return events?.length ? { events, allEvents: false } : null;
    }));
  }

  private placeAlertInFirstFreeCell(id: string): boolean {
    const tile = this.freeCellIndexes()[0];
    if (tile === undefined) return false;

    if (id === WallV2Model.WallAlertSelectorOptionId.ALL_ALERTS) {
      this.tileAlerts.update((alerts) => {
        const next = this.fitTileAlerts(alerts);
        next[tile] = { events: null, allEvents: true };
        return next;
      });
      return true;
    }

    const alert = (this.alertEvents() ?? []).find((item) => item._id === id);
    if (!alert) return false;

    const result = WallV2Model.fanOutAlertToSelectedEvents(alert, this.edgeEntities());
    if (result.missingEdge) {
      this.store$.dispatch(
        SharedActions.showMessage({
          warning: this.translate.instant('ACTIVITY_LOG.WALL_EDGE_ERROR', {
            alert_name: alert.name,
          }),
        }),
      );
    }
    if (!result.events.length) return false;

    this.tileAlerts.update((alerts) => {
      const next = this.fitTileAlerts(alerts);
      next[tile] = { events: result.events, allEvents: false };
      return next;
    });
    return true;
  }

  private resolveInitialCameras(
    cameraIds: string[],
  ): Dictionary<CameraPickerModels.SelectedCamera> {
    const next: Dictionary<CameraPickerModels.SelectedCamera> = {};
    for (const cameraId of cameraIds.slice(0, this.cameraCapacity())) {
      const camera = this.cameraEntities()[cameraId];
      if (!camera?.edgeOnly?.cameraId) continue;
      next[cameraId] = this.toSelectedCamera(camera);
    }
    return next;
  }

  private limitSelectedCameras(
    cameras: Dictionary<CameraPickerModels.SelectedCamera>,
  ): Dictionary<CameraPickerModels.SelectedCamera> {
    const next: Dictionary<CameraPickerModels.SelectedCamera> = {};
    for (const camera of Object.values(cameras).filter(
      (item): item is CameraPickerModels.SelectedCamera => !!item,
    ).slice(0, this.cameraCapacity())) {
      next[camera.cameraId] = camera;
    }
    return next;
  }

  private selectedCameraAt(index: number): CameraPickerModels.SelectedCamera | null {
    const cameraId = this.tileCameraIds()[index];
    return cameraId ? this.selectedCameras()[cameraId] ?? null : null;
  }

  private toTileCameraIds(
    cameras: Dictionary<CameraPickerModels.SelectedCamera>,
  ): (string | null)[] {
    const ids = Object.values(cameras)
      .filter((camera): camera is CameraPickerModels.SelectedCamera => !!camera?.cameraId)
      .map((camera) => camera.cameraId)
      .slice(0, this.cameraCapacity());
    const next = this.fitTileCameraIds([]);
    const existingTiles = this.baseSelectedSet().tiles ?? [];
    const availableIndexes = next
      .map((_value, index) => index)
      .filter((index) => !this.tileAlerts()[index] && !existingTiles[index]?.youtubeId);

    ids.forEach((id, index) => {
      const tile = availableIndexes[index];
      if (tile !== undefined) next[tile] = id;
    });
    return next;
  }

  private fitTileCameraIds(ids: (string | null)[]): (string | null)[] {
    const next = ids.slice(0, this.tileCount());
    while (next.length < this.tileCount()) {
      next.push(null);
    }
    return next;
  }

  private applyLayout(layout: WallV2Model.WallLayout): void {
    this.layout.set(layout);
    this.tileCameraIds.update((ids) => this.fitTileCameraIds(ids));
    this.tileAlerts.update((alerts) => this.fitTileAlerts(alerts));
    this.tileFixedSettings.update((settings) => this.fitTileFixedSettings(settings));
    this.selectedCameras.update((cameras) => this.keepCamerasUsedByTiles(cameras));
    this.commitCurrentSet();
  }

  private applyWallSettings(settings: WallV2Model.WallSettings): void {
    const nextSettings: WallV2Model.WallSettings = {
      ...this.wallSettings(),
      isPrivate: settings.isPrivate,
      tags: settings.tags ?? [],
      tileSettings: {
        ...settings.tileSettings,
      },
    };

    this.wallSettings.set(nextSettings);
    this.store$.dispatch(WallV2Actions.setWallSettings({
      isPrivate: nextSettings.isPrivate,
      tags: nextSettings.tags ?? [],
    }));
    this.store$.dispatch(WallV2Actions.setTileSettings({
      tileSettings: nextSettings.tileSettings,
    }));
  }

  private cloneWallSettings(settings: WallV2Model.WallSettings): WallV2Model.WallSettings {
    return {
      ...settings,
      tileSettings: {
        ...settings.tileSettings,
      },
      alertSettings: {
        ...settings.alertSettings,
      },
      tags: [...(settings.tags ?? [])],
    };
  }

  private fitTileFixedSettings(
    settings: (WallV2Model.TileSettings | null)[],
  ): (WallV2Model.TileSettings | null)[] {
    const next = settings
      .slice(0, this.tileCount())
      .map((setting) => setting ? { ...setting } : null);
    while (next.length < this.tileCount()) {
      next.push(null);
    }
    return next;
  }

  private fitTileAlerts(
    alerts: (WallV2Model.TileAlertData | null)[],
  ): (WallV2Model.TileAlertData | null)[] {
    const next = alerts
      .slice(0, this.tileCount())
      .map((alert) => alert
        ? {
            events: alert.events?.map((event) => ({ ...event })) ?? null,
            allEvents: alert.allEvents,
          }
        : null);
    while (next.length < this.tileCount()) {
      next.push(null);
    }
    return next;
  }

  private commitCurrentSet(): void {
    this.wallSets.set(this.committedWallSets());
    this.store$.dispatch(WallV2Actions.setSelectedWall({ wall: this.selectedWallDraft() }));
  }

  private hydrateWallForEdit(wall: WallV2Model.WallMongoDocument): void {
    const sets = wall.sets?.length
      ? wall.sets.map((set) => this.cloneWallSet(set))
      : [this.createDefaultWallSet(0)];
    const settings = this.cloneWallSettings(wall.settings ?? WallV2Model.defaultWallCreate.settings);

    this.lastHydratedSetKey = '';
    this.name.set(wall.name ?? '');
    this.wallSettings.set(settings);
    this.wallSets.set(sets);
    this.selectedSetIndex.set(0);
    // Must precede the settings dispatches below: their reducers (and the
    // set-index effects) read `selectedWall`, which is still null on first open.
    this.store$.dispatch(WallV2Actions.setSelectedWall({ wall }));
    this.store$.dispatch(WallV2Actions.setSelectedSetIndex({ index: 0 }));
    this.store$.dispatch(WallV2Actions.setWallSettings({
      isPrivate: settings.isPrivate,
      tags: settings.tags ?? [],
    }));
    this.store$.dispatch(WallV2Actions.setTileSettings({
      tileSettings: settings.tileSettings,
    }));
  }

  private buildWallUpdateDocument(
    wall: WallV2Model.WallMongoDocument,
    draft: WallV2Model.WallCreateDto,
  ): WallV2Model.WallMongoDocument {
    return {
      ...wall,
      name: draft.name,
      settings: this.cloneWallSettings(draft.settings),
      sets: draft.sets.map((set) => this.cloneWallSet(set)),
    };
  }

  private createSetFromCurrentState(index: number): WallV2Model.WallSet {
    return {
      ...this.createDefaultWallSet(index),
      layout: this.layout(),
      tiles: this.tiles(),
    };
  }

  private createDefaultWallSet(index: number): WallV2Model.WallSet {
    const layout = WallV2Model.defaultLayout;
    return {
      ...WallV2Model.defaultSet,
      name: `${this.translate.instant('ADD_SET_MODAL.DEFAULT_SET_NAME')} ${index + 1}`,
      layout,
      tiles: this.createEmptyTiles(layout),
    };
  }

  private createEmptyTiles(layout: WallV2Model.WallLayout): WallV2Model.SetTile[] {
    return new Array(WallV2Model.wallLayoutCameraCountV2[layout] || 9)
      .fill(null)
      .map(() => ({ ...WallV2Model.defaultSetTile }));
  }

  private replaceSetAt(
    sets: WallV2Model.WallSet[],
    index: number,
    set: WallV2Model.WallSet,
  ): WallV2Model.WallSet[] {
    const next = sets.length
      ? sets.map((item) => this.cloneWallSet(item))
      : [this.createDefaultWallSet(0)];

    while (next.length <= index) {
      next.push(this.createDefaultWallSet(next.length));
    }

    next[index] = this.cloneWallSet(set);
    return next;
  }

  private cloneWallSet(set: WallV2Model.WallSet): WallV2Model.WallSet {
    return {
      ...set,
      tiles: (set.tiles ?? []).map((tile) => this.cloneSetTile(tile)),
    };
  }

  private cloneSetTile(tile: WallV2Model.SetTile): WallV2Model.SetTile {
    return {
      ...tile,
      camera: tile.camera ? { ...tile.camera } : tile.camera,
      events: tile.events ? tile.events.map((event) => ({ ...event })) : tile.events,
      settings: tile.settings ? { ...tile.settings } : tile.settings,
      fixedSettings: tile.fixedSettings ? { ...tile.fixedSettings } : tile.fixedSettings,
    };
  }

  private hydrateSelectedSet(set: WallV2Model.WallSet): void {
    this.layout.set(set.layout);
    this.tileCameraIds.set(this.fitTileCameraIdsFromSet(set));
    this.tileAlerts.set(this.fitTileAlerts(
      (set.tiles ?? []).map((tile) => WallV2Model.isAlertTile(tile)
        ? {
            events: Array.isArray(tile.events)
              ? tile.events.map((event) => ({ ...event }))
              : null,
            allEvents: tile.allEvents,
          }
        : null),
    ));
    this.tileFixedSettings.set(this.fitTileFixedSettings(
      (set.tiles ?? []).map((tile) => tile?.fixedSettings ? { ...tile.fixedSettings } : null),
    ));
    this.selectedCameras.set(this.resolveCamerasFromSet(set));
  }

  private fitTileCameraIdsFromSet(set: WallV2Model.WallSet): (string | null)[] {
    const ids = (set.tiles ?? []).map((tile) => tile?.camera?.cameraId ?? null);
    return this.fitTileCameraIds(ids);
  }

  private resolveCamerasFromSet(
    set: WallV2Model.WallSet,
  ): Dictionary<CameraPickerModels.SelectedCamera> {
    const next: Dictionary<CameraPickerModels.SelectedCamera> = {};
    for (const tile of set.tiles ?? []) {
      const cameraId = tile?.camera?.cameraId;
      if (!cameraId) continue;

      const camera = this.cameraEntities()[cameraId];
      if (camera?.edgeOnly?.cameraId) {
        next[cameraId] = this.toSelectedCamera(camera);
        continue;
      }

      if (tile.camera) {
        next[cameraId] = {
          cameraId: tile.camera.cameraId,
          edgeId: tile.camera.edgeId,
          locationId: tile.camera.locationId,
        };
      }
    }
    return next;
  }

  private areWallSetsEqual(
    current: WallV2Model.WallSet,
    next: WallV2Model.WallSet,
  ): boolean {
    return JSON.stringify(current) === JSON.stringify(next);
  }

  private areTileFixedSettingsEqual(
    current: (WallV2Model.TileSettings | null)[],
    next: (WallV2Model.TileSettings | null)[],
  ): boolean {
    if (current.length !== next.length) return false;

    return current.every((setting, index) =>
      (setting?.keepRatio ?? null) === (next[index]?.keepRatio ?? null),
    );
  }

  private keepCamerasUsedByTiles(
    cameras: Dictionary<CameraPickerModels.SelectedCamera>,
  ): Dictionary<CameraPickerModels.SelectedCamera> {
    const usedIds = new Set(this.tileCameraIds().filter((id): id is string => !!id));
    const next: Dictionary<CameraPickerModels.SelectedCamera> = {};
    for (const id of usedIds) {
      if (cameras[id]) {
        next[id] = cameras[id];
      }
    }
    return next;
  }

  private toSelectedCamera(camera: EdgeCamera.CameraItem): CameraPickerModels.SelectedCamera {
    return {
      ...CameraPickerModels.transformEdgeItemCameraToSelectedCamera(camera),
      name: camera.edgeOnly?.name ?? camera.name,
      tagIds: camera.tagIds,
    };
  }
}
